import { useLocale } from "@amzn/xb-hooks";
import { ApolloClient, from, HttpLink, NormalizedCacheObject } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import fetch from "cross-fetch";
import { useRef } from "react";
import { useConfig } from "../../hooks";
import { getApolloClientCache } from "./ApolloClientCache";

export interface ApolloClientProviderConfig {
  apolloAppUri: string;
}

/**
 * WARNING! Invoking this hook will create an additional instance of the Apollo client! They all share the same
 * cache - so there is no performance hit there. But if we find that we are invoking this hook anymore than
 * we already are (2 times), then we will need to maintain a global/singleton instance of the ApolloClient outside of
 * the hook.
 */
export const useGetApolloClient = (
  name: string = "ExperienceBridgePrimaryClient",
): ApolloClient<NormalizedCacheObject> => {
  const clientRef = useRef<ApolloClient<NormalizedCacheObject> | null>(null);
  const { apolloAppUri } = useConfig();
  const { locale } = useLocale();

  clientRef.current ??= new ApolloClient({
    cache: getApolloClientCache(),
    name,
  });

  clientRef.current.setLink(
    from([
      // Retries are selfish - retry exactly once as close to the client as possible
      // https://www.apollographql.com/docs/react/api/link/apollo-link-retry
      // https://aws.amazon.com/builders-library/timeouts-retries-and-backoff-with-jitter/
      new RetryLink({
        attempts: {
          max: 2, // initial and the single retry
        },
      }),
      // HttpLink or some other terminating Link must go last https://www.apollographql.com/docs/react/api/link/introduction#the-terminating-link
      new HttpLink({
        uri: apolloAppUri as string,
        credentials: "include",
        preserveHeaderCase: true,
        fetch,
        headers: { ...(locale?.code && { "x-locale": locale.code }) },
      }),
    ]),
  );

  return clientRef.current;
};
